import React, { useEffect, useCallback, useState, useMemo } from 'react'
import { getAuthCodeUrl } from 'Helper/Auth/oauth-api'
import { getAuthCodeToken } from 'actions/authActions'
import { useDispatch, useSelector } from 'react-redux'
import AuthProviderSelector from './AuthProviderSelector.jsx'
import SummitAuth from './SummitAuthComponents/SummitAuth.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LockAccount from './LockAccount.jsx'
import globalConfig from 'Helper/configHelpers'
import { addToClickLog } from 'actions/logActions'


const AuthProviderWrapper = ({
  authProvider,
  subProvider,
  setErrorRef,
  loggedOut
}) => {
  const dispatch = useDispatch()
  const [summitLoginRequired, setSummitLoginRequired] = useState(false)
  const [isAuthError, setIsAuthError] = useState(false)
  const [getAuthCodeInProgress, setGetAuthCodeInProgress] = useState(false)
  const getAuthTokenInProgress = useSelector(
    state => state.auth.authCallInProgress
  )
  const loggedIn = useSelector(state => state.auth.token != null)
  const [searchParams] = useSearchParams()
  const [message, setMessage] = useState('')
  const navigate = useNavigate()

  const queryParams = useMemo(() => {
    return Object.fromEntries([...searchParams])
  }, [searchParams])

  const isPasswordReset =
    queryParams.token != null &&
    queryParams.id == null &&
    queryParams.hash == null &&
    queryParams.email != null &&
    queryParams.expires != null &&
    queryParams.signature != null

  const isEmailVerification =
    queryParams.token == null &&
    queryParams.id != null &&
    queryParams.hash != null &&
    queryParams.email == null &&
    queryParams.expires != null &&
    queryParams.signature != null

  const isAccountSetup =
    queryParams.token != null &&
    queryParams.id != null &&
    queryParams.hash != null &&
    queryParams.email != null &&
    queryParams.expires != null &&
    queryParams.signature != null &&
    queryParams.reset_expires != null &&
    queryParams.reset_signature != null

  useEffect(() => {
    if (queryParams.error) {
      setIsAuthError(true)
    }
  }, [queryParams.error])

  const isAccountLock =
    queryParams.lock_id != null && queryParams.signature != null
  // this is the case where the user has clicked an email link to request that their account be locked.

  const startAuthorizationGrant = useCallback(
    (authProvider, subProvider) => {
      if (getAuthCodeInProgress) {
        return
      }
      setGetAuthCodeInProgress(true)
      setSummitLoginRequired(false)
      // note... there is no special handling for authCode succeeding because if it does succeed,
      // redux auth will have a token/user_id/etc and AuthPanel will stop rendering this component.
      getAuthCodeUrl(
        authProvider,
        subProvider,
        {}, // query params aren't passed from here
        providerQueryString => {
          // we weren't web authorized so we were unable to start the oauth grant. TO fix that, we call the auth provider login route,
          // this will forward the user to the auth provider to be logged in, the auth provider will redirect them to an api callback route
          // which in turn should return a redirect back to summit with a web authorized session so that we can try again.
          if (authProvider === 'summit') {
            setSummitLoginRequired(true)
            setGetAuthCodeInProgress(false)
          } else {
            const queryParams =
              providerQueryString && providerQueryString != ''
                ? `?providerQueryString=${providerQueryString}`
                : ''
            window.location.replace(
              `${globalConfig.api_base_url}/v1/auth/${authProvider}/login${queryParams}`
            )
          }
        },
        data => {
          let state = data.url.split('state')
          state = state[1].split('&')[0].replace('=', '')
          let code = data.url.split('code')
          code = code[1].split('&')[0].replace('=', '')
          if (data.status == "200") {
            dispatch(addToClickLog({ event: `Login: ${authProvider} Login Successful` }))
          } else {
            dispatch(addToClickLog({ event: `Login: ${authProvider} Login Failed` }))
          }
          dispatch(
            getAuthCodeToken(
              { state, code },
              subProvider,
              authProvider,
              message => setErrorRef.current(message)
            )
          )
        }
      )
    },
    [authProvider, subProvider]
  )

  useEffect(() => {
    if (isAuthError) {
      setMessage(queryParams.error)
      // now that we have handled the query param, clear it.
      navigate('/login')
    }
  }, [isAuthError])

  useEffect(() => {
    if (
      !getAuthCodeInProgress &&
      !getAuthTokenInProgress &&
      !isEmailVerification &&
      !isPasswordReset &&
      !isAccountSetup &&
      !isAccountLock &&
      !isAuthError &&
      !loggedIn &&
      !isAuthError &&
      !queryParams.error &&
      !summitLoginRequired &&
      (!loggedOut || authProvider === 'summit')
      // authProvider
    ) {
      // if there is no auth provider, we use summit because technically even though the user previously may
      // have authenticated with google or someone else, it is summit-auth that may have maintained the session
      // that we are looking for.
      if (!authProvider) {
        startAuthorizationGrant('summit', subProvider)
      } else if (authProvider) {
        startAuthorizationGrant(authProvider, subProvider)
      }
    }
  }, [
    loggedIn,
    authProvider, // the first two define who is controlling auth so we need to retry if they change
    subProvider,
    summitLoginRequired // if this goes from true to false, it indicates the user logged into summit so we
    // should try auth grant to see if they are allowed oauth tokens now.
  ])

  if (
    (!authProvider &&
      !subProvider &&
      !(getAuthCodeInProgress || getAuthTokenInProgress) &&
      !loggedIn) ||
    (loggedOut && authProvider !== 'summit') ||
    isAuthError
  ) {
    return (
      <AuthProviderSelector
        authProvider={authProvider}
        subProvider={subProvider}
        startAuthorizationGrant={startAuthorizationGrant}
        message={message}
        clearAuthError={() => {
          setIsAuthError(false)
          setMessage('')
        }}
      />
    )
  } else if (
    summitLoginRequired ||
    isEmailVerification ||
    isPasswordReset ||
    isAccountSetup
  ) {
    return (
      <SummitAuth
        message={message}
        setMessage={setMessage}
        setSummitLoginRequired={setSummitLoginRequired}
        queryParams={queryParams}
        subProvider={subProvider}
        isEmailVerification={isEmailVerification}
        isPasswordReset={isPasswordReset}
        isAccountSetup={isAccountSetup}
        retryAccessTokenGrant={() => {
          startAuthorizationGrant('summit', subProvider)
        }}
      />
    )
  } else if (isAccountLock) {
    return <LockAccount />
  } else {
    return (
      <div className="login-spinner">
        <div className="login-spinner-message">Attempting authorization...</div>
        <FontAwesomeIcon
          icon={['fas', 'circle-notch']}
          spin
          size="5x"
          fixedWidth
          className="spinner-icon"
        />
      </div>
    )
  }
}
export default AuthProviderWrapper
